// © 2025 Camp Promethean, LLC. All Rights Reserved.

import { supabase } from "@/integrations/supabase/client";
import { 
  ConsultantProfile, 
  Specialty, 
  ConsultantFilters, 
  Certification, 
  Consultation, 
  Review, 
  Verification, 
  Message,
  CreateConsultantProfileRequest,
  AdminNote,
  VerificationDraft,
  VerificationStatus,
  VerificationHistoryEntry
} from "@/types/agora";
import { Json } from "@/integrations/supabase/types";
import type { Profile } from "@/types/profile";

// Helper function to transform consultation data
const transformConsultation = (data: any): Consultation => {
  // Transform client from string to Profile if needed
  const client = typeof data.client === 'string' 
    ? { id: data.client_id, first_name: '', last_name: '' } 
    : data.client;

  return {
    ...data,
    client
  };
};

function normalizeMessageRow(row: Message & { sender?: Profile | Profile[] | null }): Message {
  const s = row.sender;
  const sender = Array.isArray(s) ? s[0] ?? undefined : s ?? undefined;
  return { ...row, sender };
}

function normalizeReviewRow(row: Review & { client?: Profile | Profile[] | null }): Review {
  const c = row.client;
  const client = Array.isArray(c) ? c[0] ?? undefined : c ?? undefined;
  return { ...row, client };
}

// Helper function to transform JSON fields
const transformConsultantProfile = (data: any): ConsultantProfile => {
  // Transform available_hours from Json to the expected type
  const available_hours = data.available_hours 
    ? (typeof data.available_hours === 'string' 
        ? JSON.parse(data.available_hours) 
        : data.available_hours)
    : null;

  return {
    ...data,
    available_hours
  };
};

// Specialties
export const getSpecialties = async (): Promise<Specialty[]> => {
  const { data, error } = await supabase
    .from('agora_specialties')
    .select('*')
    .order('name');

  if (error) throw error;
  return data || [];
};

// Consultant Profiles
export const getConsultantProfiles = async (filters?: ConsultantFilters): Promise<ConsultantProfile[]> => {
  let query = supabase
    .from('agora_consultant_profiles')
    .select(`
      *,
      profile:profiles(id, first_name, last_name, avatar_url),
      specialties:agora_consultant_specialties(
        specialty_id,
        specialty:agora_specialties(*)
      ),
      certifications:agora_consultant_certifications(*)
    `)
    .eq('status', 'active');

  // Apply filters
  if (filters) {
    if (filters.minRating) {
      query = query.gte('rating', filters.minRating);
    }
    
    if (filters.maxHourlyRate) {
      query = query.lte('hourly_rate', filters.maxHourlyRate);
    }
    
    if (filters.minHourlyRate) {
      query = query.gte('hourly_rate', filters.minHourlyRate);
    }
    
    if (filters.hasVideoConferencing) {
      query = query.eq('has_video_conferencing', true);
    }
    
    if (filters.searchTerm) {
      query = query.or(`title.ilike.%${filters.searchTerm}%,bio.ilike.%${filters.searchTerm}%,short_description.ilike.%${filters.searchTerm}%`);
    }
  }

  const { data, error } = await query;

  if (error) throw error;

  // Process the data to restructure specialties
  const processedData = data?.map(consultant => {
    // Extract specialty objects from the nested structure
    const specialties = consultant.specialties
      ?.map((item: any) => item.specialty)
      .filter(Boolean);

    return transformConsultantProfile({
      ...consultant,
      specialties,
      profile: consultant.profile?.[0] || null
    });
  }) || [];

  // Filter by specialties if needed (we do this in memory since it involves a junction table)
  if (filters?.specialties?.length) {
    return processedData.filter(consultant => 
      consultant.specialties?.some((specialty: Specialty) => 
        filters.specialties?.includes(specialty.id)
      )
    );
  }

  return processedData;
};

export const getConsultantById = async (id: string): Promise<ConsultantProfile | null> => {
  // First query the consultant profile
  const { data: consultant, error: consultantError } = await supabase
    .from('agora_consultant_profiles')
    .select(`
      *,
      profile:profiles(id, first_name, last_name, avatar_url),
      certifications:agora_consultant_certifications(*)
    `)
    .eq('id', id)
    .single();

  if (consultantError) {
    if (consultantError.code === 'PGRST116') return null; // No rows found
    throw consultantError;
  }

  // Now query the specialties separately for more reliable results
  const { data: specialtyRelations, error: specialtiesError } = await supabase
    .from('agora_consultant_specialties')
    .select(`
      specialty_id,
      specialty:agora_specialties(*)
    `)
    .eq('consultant_id', id);

  if (specialtiesError) {
    console.error("Error loading specialties:", specialtiesError);
    // Continue with empty specialties
    return transformConsultantProfile({
      ...consultant,
      specialties: [],
      profile: consultant.profile?.[0] || null
    });
  }

  // Extract and transform specialty data
  const specialties = specialtyRelations
    ?.map(item => item.specialty)
    .filter(Boolean);
  
  return transformConsultantProfile({
    ...consultant,
    specialties: specialties || [],
    profile: consultant.profile?.[0] || null
  });
};

export const createConsultantProfile = async (profile: CreateConsultantProfileRequest): Promise<ConsultantProfile> => {
  console.log("Creating consultant profile with specialties:", profile.specialties);
  
  // Get the current user ID
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  
  try {
    // Create the consultant profile record
    const { data: consultant, error } = await supabase
      .from('agora_consultant_profiles')
      .insert({
        user_id: user.id,
        status: 'pending',
        title: profile.title,
        hourly_rate: profile.hourly_rate,
        years_experience: profile.years_experience,
        bio: profile.bio,
        short_description: profile.short_description,
        calendly_url: profile.calendly_url || null,
        zoom_link: profile.zoom_link || null,
        has_video_conferencing: profile.has_video_conferencing,
        available_days: profile.available_days,
        available_hours: profile.available_hours || null,
        total_reviews: 0,
        total_consultations: 0
      })
      .select()
      .single();

    if (error) {
      console.error("Error creating consultant profile:", error);
      throw error;
    }

    // Insert specialties - each specialty gets a row in the junction table
    if (profile.specialties && profile.specialties.length > 0) {
      console.log(`Inserting ${profile.specialties.length} specialties for consultant ${consultant.id}:`, 
        profile.specialties);
        
      const specialtyRows = profile.specialties.map(specialtyId => ({
        consultant_id: consultant.id,
        specialty_id: specialtyId
      }));

      const { error: specialtiesError } = await supabase
        .from('agora_consultant_specialties')
        .insert(specialtyRows);

      if (specialtiesError) {
        console.error("Error inserting specialties:", specialtiesError);
        throw specialtiesError;
      }
    } else {
      console.log("No specialties to insert");
    }

    // Create verification request for administrator review
    const { data: admins, error: adminsError } = await supabase
      .from('profiles')
      .select('id')
      .eq('role', 'admin');

    if (adminsError) {
      console.error("Error fetching admin users:", adminsError);
      // Continue without admin verification
    }

    if (admins && admins.length > 0) {
      // Create verification request assigned to a random admin
      const randomAdmin = admins[Math.floor(Math.random() * admins.length)];
      await createVerificationRequest(consultant.id, randomAdmin.id);
    } else {
      console.log("No admin users found. Creating self-verification.");
      // If no admins, create self-verification
      await createVerificationRequest(consultant.id, user.id);
    }

    // Load the full consultant profile with specialties to return
    const fullConsultant = await getConsultantById(consultant.id);
    if (!fullConsultant) {
      throw new Error("Failed to load the created consultant profile");
    }

    return fullConsultant;
  } catch (error) {
    console.error("Error in createConsultantProfile:", error);
    throw error;
  }
};

// Helper function to create a self-verification record
async function createSelfVerification(consultantId: string, userId: string) {
  const { error } = await supabase
    .from('agora_verification')
    .insert({
      consultant_id: consultantId,
      verifier_id: userId,
      status: 'pending'
    });
  
  if (error) {
    console.error("Error creating self-verification record:", error);
    // At this point, we've at least created the consultant profile
    // so we'll continue rather than throwing another error
  }
}

export const updateConsultantProfile = async (id: string, profile: Partial<Omit<ConsultantProfile, 'id' | 'user_id' | 'status' | 'created_at' | 'updated_at' | 'rating' | 'total_reviews' | 'total_consultations'>>): Promise<ConsultantProfile> => {
  const { data, error } = await supabase
    .from('agora_consultant_profiles')
    .update(profile)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return transformConsultantProfile(data);
};

// Certifications
export const addCertification = async (consultantId: string, certification: Omit<Certification, 'id' | 'consultant_id' | 'verified' | 'created_at' | 'updated_at'>, document?: File): Promise<Certification> => {
  // Upload document if provided
  let documentUrl = null;
  if (document) {
    const fileExt = document.name.split('.').pop();
    const fileName = `${consultantId}/${Math.random().toString(36).substring(2)}.${fileExt}`;
    
    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from('certifications')
      .upload(fileName, document);

    if (uploadError) throw uploadError;
    
    // Get public URL
    const { data: { publicUrl } } = supabase
      .storage
      .from('certifications')
      .getPublicUrl(fileName);
      
    documentUrl = publicUrl;
  }

  // Insert certification
  const { data, error } = await supabase
    .from('agora_consultant_certifications')
    .insert({
      ...certification,
      consultant_id: consultantId,
      document_url: documentUrl,
      verified: false
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const removeCertification = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('agora_consultant_certifications')
    .delete()
    .eq('id', id);

  if (error) throw error;
};

// Consultations
export const createConsultation = async (consultation: Omit<Consultation, 'id' | 'status' | 'created_at' | 'updated_at' | 'consultant_payout' | 'platform_fee'>): Promise<Consultation> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Calculate platform fee (20% of total amount)
  const platformFee = consultation.total_amount * 0.2;
  const consultantPayout = consultation.total_amount - platformFee;

  const { data, error } = await supabase
    .from('agora_consultations')
    .insert({
      ...consultation,
      client_id: user.id,
      status: 'scheduled',
      platform_fee: platformFee,
      consultant_payout: consultantPayout
    })
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const getConsultationById = async (id: string): Promise<Consultation | null> => {
  const { data, error } = await supabase
    .from('agora_consultations')
    .select(`
      *,
      consultant:agora_consultant_profiles(
        *,
        profile:profiles(id, first_name, last_name, avatar_url)
      ),
      client:profiles!agora_consultations_client_id_fkey(id, first_name, last_name, avatar_url),
      messages:agora_messages(
        *,
        sender:profiles!agora_messages_sender_id_fkey(id, first_name, last_name, avatar_url)
      ),
      review:agora_reviews(
        *,
        client:profiles!agora_reviews_client_id_fkey(id, first_name, last_name, avatar_url)
      )
    `)
    .eq('id', id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No rows found
    throw error;
  }

  const messages = Array.isArray(data.messages)
    ? data.messages.map(normalizeMessageRow)
    : undefined;

  // Process nested data
  return transformConsultation({
    ...data,
    messages,
    consultant: data.consultant?.[0] 
      ? {
          ...data.consultant[0],
          profile: data.consultant[0].profile?.[0] || null
        }
      : null,
    client: data.client?.[0] || null,
    review: data.review?.[0] ? normalizeReviewRow(data.review[0]) : null
  });
};

export const getConsultations = async (userId: string, isConsultant: boolean = false): Promise<Consultation[]> => {
  const field = isConsultant ? 'consultant_id' : 'client_id';
  
  const { data, error } = await supabase
    .from('agora_consultations')
    .select(`
      *,
      consultant:agora_consultant_profiles(
        *,
        profile:profiles(id, first_name, last_name, avatar_url)
      ),
      client:profiles!agora_consultations_client_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .eq(field, userId)
    .order('start_time', { ascending: false });

  if (error) throw error;

  // Process the data
  return (data || []).map(consultation => {
    return transformConsultation({
      ...consultation,
      consultant: consultation.consultant?.[0] 
        ? {
            ...consultation.consultant[0],
            profile: consultation.consultant[0].profile?.[0] || null
          }
        : null,
      client: consultation.client?.[0] || null,
    });
  });
};

export const updateConsultationStatus = async (id: string, status: Consultation['status'], reason?: string): Promise<Consultation> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const updateData: any = { status };
  if (status === 'cancelled' && reason) {
    updateData.cancellation_reason = reason;
    updateData.cancelled_by = user.id;
  }

  const { data, error } = await supabase
    .from('agora_consultations')
    .update(updateData)
    .eq('id', id)
    .select(`
      *,
      consultant:agora_consultant_profiles(
        *,
        profile:profiles(id, first_name, last_name, avatar_url)
      ),
      client:profiles!agora_consultations_client_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .single();

  if (error) throw error;

  // Process nested data
  return transformConsultation({
    ...data,
    consultant: data.consultant?.[0] ? {
      ...data.consultant[0],
      profile: data.consultant[0].profile?.[0] || null
    } : null,
    client: data.client?.[0] || null
  });
};

// Reviews
export const submitReview = async (consultationId: string, rating: number, reviewText?: string): Promise<Review> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get consultation and consultant details
  const { data: consultation, error: consultationError } = await supabase
    .from('agora_consultations')
    .select('id, consultant_id')
    .eq('id', consultationId)
    .eq('client_id', user.id)
    .eq('status', 'completed')
    .single();

  if (consultationError) throw consultationError;

  // Insert review
  const { data, error } = await supabase
    .from('agora_reviews')
    .insert({
      consultation_id: consultationId,
      consultant_id: consultation.consultant_id,
      client_id: user.id,
      rating,
      review_text: reviewText?.trim() ? reviewText.trim() : null,
    })
    .select(`
      *,
      client:profiles!agora_reviews_client_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .single();

  if (error) {
    if (error.code === '23505') {
      throw new Error('You have already submitted a review for this consultation.');
    }
    throw error;
  }
  return normalizeReviewRow(data);
};

export const updateReview = async (
  reviewId: string,
  rating: number,
  reviewText?: string | null,
): Promise<Review> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('agora_reviews')
    .update({
      rating,
      review_text: reviewText?.trim() ? reviewText.trim() : null,
    })
    .eq('id', reviewId)
    .select(`
      *,
      client:profiles!agora_reviews_client_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .single();

  if (error) throw error;
  return normalizeReviewRow(data);
};

export const getConsultantReviews = async (
  consultantId: string,
  limit = 30,
): Promise<Review[]> => {
  const { data, error } = await supabase
    .from('agora_reviews')
    .select(`
      *,
      client:profiles!agora_reviews_client_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .eq('consultant_id', consultantId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []).map((row) =>
    normalizeReviewRow(row as Review & { client?: Profile | Profile[] | null }),
  );
};

// Verification
export const getVerificationRequests = async (): Promise<Verification[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Check if user is admin
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  if (profileError) throw profileError;

  // Query the verification table
  const query = supabase
    .from('agora_verification')
    .select(`
      *,
      consultant_id
    `)
    .eq('status', 'pending');

  // Only filter by verifier_id if the user is not an admin
  if (profile.role !== 'admin') {
    query.eq('verifier_id', user.id);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error getting verification requests:", error);
    throw error;
  }

  if (!data || data.length === 0) {
    return [];
  }

  // Now fetch the full consultant profiles with specialties for each verification request
  const verificationRequests: Verification[] = await Promise.all(
    data.map(async (verification): Promise<Verification> => {
      try {
        // If there's a consultant ID, load the full profile with specialties
        if (verification.consultant_id) {
          const consultant = await getConsultantById(verification.consultant_id);
          if (consultant) {
            console.log(`Loaded consultant for verification ${verification.id}:`, 
              { id: consultant.id, specialties: consultant.specialties });
            return {
              ...verification,
              consultant
            };
          }
        }
        
        // Return verification without consultant if not found
        return verification;
      } catch (error) {
        console.error(`Error loading consultant for verification ${verification.id}:`, error);
        return verification;
      }
    })
  );

  return verificationRequests;
};

export const submitVerification = async (
  verificationId: string, 
  status: 'approved' | 'rejected', 
  comments?: string
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data: actorProfile, error: actorProfileError } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  if (actorProfileError) throw actorProfileError;

  const isPrivilegedVerifier =
    actorProfile?.role === 'admin' || actorProfile?.role === 'moderator';

  let updateQuery = supabase
    .from('agora_verification')
    .update({
      status,
      comments
    })
    .eq('id', verificationId);

  if (!isPrivilegedVerifier) {
    updateQuery = updateQuery.eq('verifier_id', user.id);
  }

  const { data: updatedVerification, error: updateError } = await updateQuery
    .select('id')
    .maybeSingle();

  if (updateError) throw updateError;
  if (!updatedVerification) {
    throw new Error(
      'Could not update this verification. You may not be the assigned verifier, or the request no longer exists.'
    );
  }

  // Get the consultant ID
  const { data: verification, error: verificationError } = await supabase
    .from('agora_verification')
    .select('consultant_id')
    .eq('id', verificationId)
    .single();

  if (verificationError) throw verificationError;

  // Update consultant status based on verification result
  const { error: consultantError } = await supabase
    .from('agora_consultant_profiles')
    .update({
      status: status === 'approved' ? 'active' : 'rejected',
      updated_at: new Date().toISOString()
    })
    .eq('id', verification.consultant_id);

  if (consultantError) throw consultantError;
  
  // Add to verification history
  await addVerificationHistoryEntry(
    verificationId,
    `verification_${status}`,
    { comments }
  );
  
  // Remove any draft after successful submission
  try {
    await supabase
      .from('agora_verification_drafts')
      .delete()
      .eq('verification_id', verificationId)
      .eq('admin_id', user.id);
  } catch (error) {
    console.error("Error removing verification draft:", error);
    // Continue even if draft removal fails
  }
};

// Messages
export const getConsultationMessages = async (consultationId: string): Promise<Message[]> => {
  const { data, error } = await supabase
    .from('agora_messages')
    .select(`
      *,
      sender:profiles!agora_messages_sender_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .eq('consultation_id', consultationId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data || []).map(normalizeMessageRow);
};

export const sendMessage = async (consultationId: string, message: string): Promise<Message> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { data, error } = await supabase
    .from('agora_messages')
    .insert({
      consultation_id: consultationId,
      sender_id: user.id,
      message
    })
    .select(`
      *,
      sender:profiles!agora_messages_sender_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .single();

  if (error) throw error;
  return normalizeMessageRow(data);
};

export const markMessagesAsRead = async (consultationId: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  const { error } = await supabase
    .from('agora_messages')
    .update({ read: true })
    .eq('consultation_id', consultationId)
    .neq('sender_id', user.id)
    .eq('read', false);

  if (error) throw error;
};

// Get user consultant profile (if exists)
export const getUserConsultantProfile = async (): Promise<ConsultantProfile | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('agora_consultant_profiles')
    .select(`
      *,
      specialties:agora_consultant_specialties(
        specialty_id,
        specialty:agora_specialties(*)
      ),
      certifications:agora_consultant_certifications(*)
    `)
    .eq('user_id', user.id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No rows found
    throw error;
  }

  // Process the data to restructure specialties
  const specialties = data.specialties
    ?.map((item: any) => item.specialty)
    .filter(Boolean);

  return {
    ...data,
    specialties,
  };
};

export const getConsultantByUserId = async (userId: string): Promise<ConsultantProfile | null> => {
  const { data, error } = await supabase
    .from('agora_consultant_profiles')
    .select(`
      *,
      specialties:agora_consultant_specialties(
        specialty_id,
        specialty:agora_specialties(*)
      ),
      certifications:agora_consultant_certifications(*)
    `)
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // No rows found
    throw error;
  }

  // Process the data to restructure specialties
  const specialties = data.specialties
    ?.map((item: any) => item.specialty)
    .filter(Boolean);

  return transformConsultantProfile({
    ...data,
    specialties,
    certifications: data.certifications || []
  });
};

export const getUserConsultations = async (): Promise<Consultation[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Check if the user is a consultant
  const { data: consultantProfile, error: consultantError } = await supabase
    .from('agora_consultant_profiles')
    .select('id')
    .eq('user_id', user.id)
    .single();

  if (consultantError && consultantError.code !== 'PGRST116') throw consultantError;

  let query = supabase
    .from('agora_consultations')
    .select(`
      *,
      consultant:agora_consultant_profiles(
        *,
        profile:profiles(id, first_name, last_name, avatar_url)
      ),
      client:profiles!agora_consultations_client_id_fkey(id, first_name, last_name, avatar_url)
    `);

  if (consultantProfile) {
    // User is a consultant, fetch both as client and consultant
    query = query.or(`client_id.eq.${user.id},consultant_id.eq.${consultantProfile.id}`);
  } else {
    // User is only a client
    query = query.eq('client_id', user.id);
  }

  const { data, error } = await query.order('start_time', { ascending: false });

  if (error) throw error;

  // Process nested data
  return (data || []).map(consultation => 
    transformConsultation({
      ...consultation,
      consultant: consultation.consultant?.[0] ? {
        ...consultation.consultant[0],
        profile: consultation.consultant[0].profile?.[0] || null
      } : null,
      client: consultation.client?.[0] || null
    })
  );
};

export const bookConsultation = async (booking: any): Promise<Consultation> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');

  // Get consultant profile to calculate fees
  const { data: consultant, error: consultantError } = await supabase
    .from('agora_consultant_profiles')
    .select('*')
    .eq('id', booking.consultant_id)
    .single();

  if (consultantError) throw consultantError;

  // Calculate duration in minutes
  const startTime = new Date(booking.start_time);
  const endTime = new Date(booking.end_time);
  const durationMinutes = Math.round((endTime.getTime() - startTime.getTime()) / (1000 * 60));

  // Calculate fees
  const hourlyRate = consultant.hourly_rate;
  const totalAmount = (hourlyRate / 60) * durationMinutes;
  const platformFee = totalAmount * 0.15; // 15% platform fee
  const consultantPayout = totalAmount - platformFee;

  // Insert consultation
  const { data, error } = await supabase
    .from('agora_consultations')
    .insert({
      consultant_id: booking.consultant_id,
      client_id: user.id,
      title: booking.title,
      description: booking.description || null,
      start_time: booking.start_time,
      end_time: booking.end_time,
      duration_minutes: durationMinutes,
      status: 'scheduled',
      hourly_rate: hourlyRate,
      total_amount: totalAmount,
      platform_fee: platformFee,
      consultant_payout: consultantPayout,
      payment_intent_id: booking.payment_method_id || null,
      payment_status: "unpaid" as const,
    })
    .select(`
      *,
      consultant:agora_consultant_profiles(
        *,
        profile:profiles(id, first_name, last_name, avatar_url)
      ),
      client:profiles!agora_consultations_client_id_fkey(id, first_name, last_name, avatar_url)
    `)
    .single();

  if (error) throw error;

  // Process nested data
  return transformConsultation({
    ...data,
    consultant: data.consultant?.[0] ? {
      ...data.consultant[0],
      profile: data.consultant[0].profile?.[0] || null
    } : null,
    client: data.client?.[0] || null
  });
};

/** Stripe Checkout for consultation total (Phase 3 — platform balance; Connect later). */
export const createAgoraCheckoutForConsultation = async (
  consultationId: string,
): Promise<void> => {
  const { error: userError } = await supabase.auth.getUser();
  if (userError) {
    throw new Error("You must be signed in to open Stripe Checkout.");
  }
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error("You must be signed in to open Stripe Checkout.");
  }

  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  const { data, error } = await supabase.functions.invoke<{
    url?: string;
    error?: string;
  }>("agora-create-checkout", {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
    body: {
      consultationId,
      successUrl: `${origin}/agora/professionals/consultations/${consultationId}?checkout=success`,
      cancelUrl: `${origin}/agora/professionals/consultations/${consultationId}?checkout=cancel`,
    },
  });
  if (error) throw error;
  const msg = data && "error" in data && data.error;
  if (msg) throw new Error(String(msg));
  const url = data?.url;
  if (!url) throw new Error("Checkout did not return a URL.");
  window.location.href = url;
};

// Helper function to create a verification request
async function createVerificationRequest(consultantId: string, verifierId: string) {
  const { error } = await supabase
    .from('agora_verification')
    .insert({
      consultant_id: consultantId,
      verifier_id: verifierId,
      status: 'pending'
    });

  if (error) {
    console.error("Error creating verification request:", error);
    throw error;
  }
}

// Get admin dashboard statistics
export const getAdminDashboardStats = async () => {
  try {
    // Get total active consultants
    const { count: totalConsultants, error: consultantsError } = await supabase
      .from('agora_consultant_profiles')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active');
    
    if (consultantsError) throw consultantsError;
    
    // Get total consultations
    const { count: totalConsultations, error: consultationsError } = await supabase
      .from('agora_consultations')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'completed');
    
    if (consultationsError) throw consultationsError;
    
    // Get pending verification requests
    const { count: pendingApplications, error: pendingError } = await supabase
      .from('agora_verification')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'pending');
    
    if (pendingError) throw pendingError;
    
    // Get recent registrations (last 7 days)
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    const { count: recentRegistrations, error: registrationsError } = await supabase
      .from('agora_consultant_profiles')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', sevenDaysAgo.toISOString());
    
    if (registrationsError) throw registrationsError;
    
    // Return all stats
    return {
      totalConsultants: totalConsultants || 0,
      totalConsultations: totalConsultations || 0,
      pendingApplications: pendingApplications || 0,
      recentReports: 0, // Hardcoded until we have a reports table
      recentRegistrations: recentRegistrations || 0
    };
  } catch (error) {
    console.error("Error fetching admin dashboard stats:", error);
    // Return default values in case of error
    return {
      totalConsultants: 0,
      totalConsultations: 0,
      pendingApplications: 0,
      recentReports: 0,
      recentRegistrations: 0
    };
  }
};

// Admin Notes
export const getAdminNotes = async (verificationId: string): Promise<AdminNote[]> => {
  const { data, error } = await supabase
    .from('agora_admin_notes')
    .select('*')
    .eq('verification_id', verificationId)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const addAdminNote = async (verificationId: string, note: string, isPrivate: boolean = true): Promise<AdminNote> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from('agora_admin_notes')
    .insert({
      verification_id: verificationId,
      admin_id: user.id,
      note,
      is_private: isPrivate
    })
    .select()
    .single();
  
  if (error) throw error;
  return data;
};

// Verification Drafts
export const getVerificationDraft = async (verificationId: string): Promise<VerificationDraft | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from('agora_verification_drafts')
    .select('*')
    .eq('verification_id', verificationId)
    .eq('admin_id', user.id)
    .maybeSingle();
  
  if (error) throw error;
  return data;
};

export const saveVerificationDraft = async (
  verificationId: string, 
  decision: VerificationStatus,
  comments?: string
): Promise<VerificationDraft> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  
  // Update or insert if not exists (upsert)
  const { data, error } = await supabase
    .from('agora_verification_drafts')
    .upsert({
      verification_id: verificationId,
      admin_id: user.id,
      decision,
      draft_comments: comments || null,
      last_reviewed_at: new Date().toISOString()
    }, { onConflict: 'verification_id,admin_id' })
    .select()
    .single();
  
  if (error) throw error;
  
  // Add history entry for draft save
  await addVerificationHistoryEntry(
    verificationId, 
    'draft_saved',
    { decision, comments }
  );
  
  return data;
};

// Verification History
export const getVerificationHistory = async (verificationId: string): Promise<VerificationHistoryEntry[]> => {
  const { data, error } = await supabase
    .from('agora_verification_history')
    .select(`
      *,
      admin:profiles(id, first_name, last_name, avatar_url)
    `)
    .eq('verification_id', verificationId)
    .order('created_at', { ascending: false });
  
  if (error) throw error;
  return data || [];
};

export const addVerificationHistoryEntry = async (
  verificationId: string,
  action: string,
  details?: any
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from('agora_verification_history')
    .insert({
      verification_id: verificationId,
      admin_id: user.id,
      action,
      details: details || null
    });
  
  if (error) throw error;
}; 